// Inclusive-tax breakdown row label helpers.
//
// Sophie's design (CURRENT-1672, 2026-06-08): when a cart/order is priced
// inclusive of tax, the breakdown grid renders an informational row between
// Shipping and Total like "Tax 10% GST (included in pricing)  $1.02".
//
// The rate originates from `country.tax_standard_rate` on the cart blueprint
// (added in fluid-main PR #18246). The order blueprinter does not yet expose
// the country, so order surfaces gracefully degrade to "Tax GST (included
// in pricing)" — no rate — until the backend grows the field.

// Computes the gross (catalog) subtotal shown for inclusive orders/carts.
//
// Backend `sub_total` is the NET when inclusive; the catalog figure is
// `Net + item Tax`. Since the backend `tax` is the COMBINED items + shipping
// VAT, the embedded shipping VAT is subtracted back out — it belongs to the
// Shipping line, not the item subtotal. `shippingVat` is 0 when absent
// (exclusive pricing, or backends that predate the field).
export function computeInclusiveGrossSubtotal(
  netSubtotal: number,
  tax: number,
  shippingVat: number,
): number {
  const safeShippingVat = Number.isFinite(shippingVat) ? shippingVat : 0;
  return netSubtotal + tax - safeShippingVat;
}

export type InclusiveTaxLabelParams = {
  rate: string | null;
  name: string;
};

// Formats a tax_standard_rate (percent) for display. Whole numbers render
// without a decimal (10), fractional rates render with one decimal (8.5).
// Returns null when the rate is missing, non-finite, or non-positive.
export function formatInclusiveTaxRate(
  rate: number | null | undefined,
): string | null {
  if (rate == null || !Number.isFinite(rate) || rate <= 0) return null;
  return Number.isInteger(rate) ? String(rate) : rate.toFixed(1);
}

// Resolves the label params (rate, name) for the inclusive-tax row, or
// null when the row should not render (exclusive pricing or missing name).
export function getInclusiveTaxLabelParams(input: {
  priceInclusiveOfTax: boolean | null | undefined;
  priceInclusiveTaxName: string | null | undefined;
  taxStandardRate?: number | null;
}): InclusiveTaxLabelParams | null {
  if (!input.priceInclusiveOfTax) return null;
  const name = input.priceInclusiveTaxName?.trim();
  if (!name) return null;
  return { name, rate: formatInclusiveTaxRate(input.taxStandardRate) };
}

// Resolves whether the inclusive breakdown (gross subtotal + "included in
// pricing" tax row) can be rendered for a given pricing payload.
//
// The contract is "Subtotal (gross) = Net + item Tax" — the caller derives
// gross from `Net + Tax` and subtracts the embedded shipping VAT
// (`inclusive_shipping_tax`), which belongs to the Shipping line. This helper
// only gates rendering, so `netSubtotal` and `tax` must be present and finite.
// When either is missing (e.g. older order blueprint
// payloads where `order.subtotal` is null), the caller MUST NOT fall back
// to a NET-only subtotal display while still rendering the "included in
// pricing" tax row — that produces contradictory math:
//
//   Subtotal $10.21 (NET) + Tax $1.02 (included) = Total $11.23
//
// Instead, callers should suppress the entire inclusive breakdown and let
// the legacy exclusive Tax row render alongside the NET subtotal, which is
// mathematically consistent.
//
// `priceInclusiveTaxName` is ALSO required: without a non-blank name there
// is no way to render the explanatory "Tax {name} (included in pricing)"
// row, and the gross subtotal alone would leave customers with an
// unexplained inflated figure. Both signals — gross subtotal and tax row —
// must travel together. If we can't show the label, fall back to NET +
// exclusive Tax row.
export function canRenderInclusiveBreakdown(input: {
  priceInclusiveOfTax: boolean | null | undefined;
  priceInclusiveTaxName: string | null | undefined;
  netSubtotal: number | null | undefined;
  tax: number | null | undefined;
}): boolean {
  if (input.priceInclusiveOfTax !== true) return false;
  if (!input.priceInclusiveTaxName?.trim()) return false;
  if (input.netSubtotal == null || !Number.isFinite(input.netSubtotal)) {
    return false;
  }
  if (input.tax == null || !Number.isFinite(input.tax)) return false;
  return true;
}
